'use client'

import Image from 'next/image'
import holdfastBayLogo from '@/images/logos/holdfastbay.svg'
import marionLogo from '@/images/logos/marion.svg'
import onkaparingaLogo from '@/images/logos/onkaparinga.png'
import { cn } from '@/utils/cn'

interface ServiceAreasProps {
  variant?: 'light' | 'dark'
  className?: string
}

const areas = [
  {
    name: 'Holdfast Bay',
    logo: holdfastBayLogo,
    suburbs: 'Glenelg, Brighton, Somerton Park',
  },
  {
    name: 'Marion',
    logo: marionLogo,
    suburbs: 'Morphett Vale, Hallett Cove, Seacliff',
  },
  {
    name: 'Onkaparinga',
    logo: onkaparingaLogo,
    suburbs: 'Reynella, Old Reynella, Happy Valley',
  },
]

export default function ServiceAreas({
  variant = 'light',
  className,
}: ServiceAreasProps) {
  const isDark = variant === 'dark'

  return (
    <section
      className={cn(
        'py-12 sm:py-16',
        isDark ? 'bg-gray-900' : 'bg-gray-50',
        className
      )}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className={cn(
              'text-2xl font-bold tracking-tight sm:text-3xl',
              isDark ? 'text-white' : 'text-gray-900'
            )}
          >
            Proudly Serving Adelaide&apos;s Southern Suburbs
          </h2>
          <p
            className={cn(
              'mt-3 text-base sm:text-lg',
              isDark ? 'text-gray-300' : 'text-gray-600'
            )}
          >
            Bringing world-class music education to your neighbourhood.
            Trusted by families across three councils. Holdfast Bay, Marion & Onkaparinga.
          </p>
        </div>

        {/* Logos */}
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 items-center gap-8 sm:grid-cols-3 sm:gap-12">
          {areas.map((area) => (
            <div key={area.name} className="flex flex-col items-center">
              <div
                className={cn(
                  'flex h-20 w-full items-center justify-center rounded-lg px-4 shadow-sm',
                  isDark ? 'bg-white/10' : 'bg-white'
                )}
              >
                <Image
                  src={area.logo}
                  alt={`${area.name} Council`}
                  className="h-12 w-auto object-contain sm:h-14 rounded-md "
                  width={200}
                  height={56}
                />
              </div>
              <p
                className={cn(
                  'mt-3 text-center text-sm',
                  isDark ? 'text-gray-400' : 'text-gray-500'
                )}
              >
                {area.suburbs}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom tagline */}
        <p
          className={cn(
            'mt-10 text-center text-sm font-medium',
            isDark ? 'text-green-400' : 'text-green-700'
          )}
        >
          🎵 Bringing world-class music lessons to your neighbourhood
        </p>
      </div>
    </section>
  )
}
